import { Card as LegacyCard } from 'react-bootstrap';
import { Card } from 'components/ui/card';
import { cn } from 'lib/utils';

/**
 * Card used by the driver-feature clusters.
 *
 * Two variants during the shadcn migration:
 *  - default  : the original react-bootstrap card with the legacy
 *               .card-feature-component / .card-header-feature CSS. Those rules are
 *               `width: fit-content`, which is why call sites all pass a different
 *               inline width to fight them.
 *  - "modern" : shadcn Card + Tailwind. Fills its grid cell, no inline widths.
 *
 * Opt-in per screen so a pilot page can be evaluated without moving the other
 * Astro screens. Once accepted, make "modern" the default and delete the legacy
 * branch — the five callers already pass everything it needs.
 */
const FeatureCard = ({ title, className, style, bodyStyle, bodyClassName, bodyLayout, variant, children }) => {
    if (variant === 'modern') {
        // bodyLayout="fields": a shared 3-track grid (label / control / unit) that all
        // FieldRow children join via `display: contents`, so controls and units line up
        // down the card instead of each row sizing its own columns.
        // Anything else (charts, the reference-schedule table) gets a plain block body
        // and keeps its own internal spacing.
        const body = bodyLayout === 'fields'
            ? 'grid grid-cols-[minmax(5rem,1fr)_auto_auto] items-center gap-x-3 gap-y-2.5'
            : 'space-y-3';

        return (
            <Card className={cn('flex h-full w-full flex-col overflow-hidden', className)} style={style}>
                {/* Guarded: some cards are pure content (an info alert, a chart) and an
                    empty bordered header bar would just be a stray rule across the top. */}
                {title != null && (
                    <div className="border-b border-border px-4 py-2.5 text-sm font-semibold leading-tight">
                        {title}
                    </div>
                )}
                <div className={cn('flex-1 px-4 py-3', body, bodyClassName)} style={bodyStyle}>
                    {children}
                </div>
            </Card>
        );
    }

    return (
        <LegacyCard className={className ?? 'card-feature-component'} style={style}>
            <div className="card-header-feature">
                {title}
            </div>
            <LegacyCard.Body className="card-body-feature" style={bodyStyle ?? { paddingTop: '0.75rem' }}>
                {children}
            </LegacyCard.Body>
        </LegacyCard>
    );
};

export default FeatureCard;
